import { httpClient } from '@/core/api/http-client'
import type { Discount, DiscountInput, DiscountListParams } from '@/domain/entities/discount.entity'
import type { Paged } from '@/domain/entities/paging'
import type { DiscountRepository } from '@/domain/repositories/discount.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { DiscountDto } from './discount.dto'
import { discountMapper } from './discount.mapper'

export class DiscountHttpRepository implements DiscountRepository {
  async list(params: DiscountListParams, outletId?: string): Promise<Paged<Discount>> {
    const raw = await httpClient.get<unknown>('/discounts', {
      params: {
        // outlet_id opsional: jika diberikan, BE filter diskon yang berlaku di outlet itu.
        // Tanpa outlet_id, seluruh diskon tenant dikembalikan.
        ...(outletId ? { outlet_id: outletId } : {}),
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
        // Absen = semua status. Nilai tak dikenal ditolak BE (strconv.ParseBool).
        active: params.active ?? undefined,
        scope: params.scope ?? undefined,
      },
    })
    const paged = unwrapPaged<DiscountDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(discountMapper.toDiscount) }
  }

  async create(input: DiscountInput): Promise<Discount> {
    const raw = await httpClient.post<unknown>('/discounts', discountMapper.toCreateBody(input))
    return discountMapper.toDiscount(unwrap<DiscountDto>(raw))
  }

  async update(id: string, input: DiscountInput): Promise<Discount> {
    const raw = await httpClient.put<unknown>(
      `/discounts/${id}`,
      discountMapper.toUpdateBody(input),
    )
    return discountMapper.toDiscount(unwrap<DiscountDto>(raw))
  }

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/discounts/${id}`)
  }

  async restore(id: string): Promise<Discount> {
    // Hanya `active` yang dikirim: field lain dibiarkan apa adanya, dan
    // `max_amount` yang tidak disebut tidak akan tersentuh.
    const raw = await httpClient.put<unknown>(`/discounts/${id}`, { active: true })
    return discountMapper.toDiscount(unwrap<DiscountDto>(raw))
  }

  async findByCouponCode(code: string): Promise<Discount> {
    const raw = await httpClient.get<unknown>(`/discounts/coupon/${encodeURIComponent(code)}`)
    return discountMapper.toDiscount(unwrap<DiscountDto>(raw))
  }
}
